import React from "react";
import { Icon } from "../../components/Component";

// Renders one schema field and reports changes upward as (newValue).
// Recursive for groups and object lists.
const FieldEditor = ({ field, value, onChange }) => {
  switch (field.type) {
    case "textarea":
      return (
        <Field label={field.label}>
          <textarea className="form-control" rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );

    case "number":
      return (
        <Field label={field.label}>
          <input
            type="number"
            className="form-control"
            style={{ maxWidth: 160 }}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          />
        </Field>
      );

    case "cta":
      return (
        <Field label={field.label}>
          <div className="hc-grid-2">
            <input
              className="form-control"
              placeholder="Button text"
              value={value?.text ?? ""}
              onChange={(e) => onChange({ ...value, text: e.target.value })}
            />
            <input
              className="form-control"
              placeholder="Link, e.g. /perfumes"
              value={value?.link ?? ""}
              onChange={(e) => onChange({ ...value, link: e.target.value })}
            />
          </div>
        </Field>
      );

    case "group":
      return (
        <div className="hc-group">
          <div className="hc-group-title">{field.label}</div>
          {field.fields.map((child) => (
            <FieldEditor
              key={child.key}
              field={child}
              value={value?.[child.key]}
              onChange={(v) => onChange({ ...value, [child.key]: v })}
            />
          ))}
        </div>
      );

    case "list":
      return (
        <Field label={field.label}>
          <ListEditor
            items={value ?? []}
            emptyItem=""
            onChange={onChange}
            renderRow={(item, i, update, actions) => (
              <div className="hc-list-row" key={i}>
                {field.multiline ? (
                  <textarea className="form-control hc-list-input" rows={3} value={item} onChange={(e) => update(e.target.value)} />
                ) : (
                  <input className="form-control hc-list-input" value={item} onChange={(e) => update(e.target.value)} />
                )}
                {actions}
              </div>
            )}
          />
        </Field>
      );

    case "objectList":
      return (
        <Field label={field.label}>
          <ListEditor
            items={value ?? []}
            emptyItem={Object.fromEntries(field.fields.map((f) => [f.key, ""]))}
            onChange={onChange}
            renderRow={(item, i, update, actions) => (
              <div className="hc-list-item" key={i}>
                <div className="hc-list-item-head">
                  <span className="hc-list-item-title">Item {i + 1}</span>
                  {actions}
                </div>
                <div className="hc-list-item-body">
                  {field.fields.map((child) => (
                    <FieldEditor
                      key={child.key}
                      field={child}
                      value={item[child.key]}
                      onChange={(v) => update({ ...item, [child.key]: v })}
                    />
                  ))}
                </div>
              </div>
            )}
          />
        </Field>
      );

    default:
      return (
        <Field label={field.label}>
          <input className="form-control" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
  }
};

const Field = ({ label, children }) => (
  <div className="hc-field">
    <label className="hc-label">{label}</label>
    {children}
  </div>
);

// Generic add / remove / reorder list. Every operation is O(n) on a small list.
const ListEditor = ({ items, emptyItem, onChange, renderRow }) => {
  const update = (index, next) => onChange(items.map((item, i) => (i === index ? next : item)));
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const actionsFor = (i) => (
    <div className="hc-actions">
      <button type="button" className="hc-icon-btn" onClick={() => move(i, -1)} disabled={i === 0} title="Move up">
        <Icon name="arrow-up" />
      </button>
      <button type="button" className="hc-icon-btn" onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Move down">
        <Icon name="arrow-down" />
      </button>
      <button type="button" className="hc-icon-btn is-danger" onClick={() => remove(i)} title="Remove">
        <Icon name="trash" />
      </button>
    </div>
  );

  return (
    <>
      {items.length > 0 && (
        <div className="hc-list">{items.map((item, i) => renderRow(item, i, (next) => update(i, next), actionsFor(i)))}</div>
      )}
      <button type="button" className="hc-add-btn" onClick={() => onChange([...items, emptyItem])}>
        <Icon name="plus" /> Add
      </button>
    </>
  );
};

export default FieldEditor;
